import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLazyQuery, useMutation, useSubscription } from '@apollo/client';
import {
	AppState,
	Modal,
	Platform,
	Pressable,
	StatusBar,
	TouchableOpacity,
	Vibration,
	View,
	useWindowDimensions,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigationState } from '@react-navigation/native';

import { CLOCK_EVENT_ACTION } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';
import {
	CREATE_CLOCK_QR_TOKEN,
	GET_MY_CLOCK_PASS_SCAN_STATE,
	SUBSCRIBE_TO_CLOCK_EVENT_SCAN,
} from '../../../common/graphql/clockEvent';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../../components/text/Text';
import { EmptyState, Screen, SecondaryButton } from '../../design/components';
import { useDriverDay, useDriverShell } from '../driver/DriverDayProvider';
import { APP_TABS, ROUTE_TAB } from '../driver/driverRoutes';
import ClockOutStepsWarning from './ClockOutStepsWarning';
import { createClockPassViewStyles } from './ClockPassView.styles';
import ShiftSummaryModal from './ShiftSummaryModal';
import useQrPreviewBrightness from './useQrPreviewBrightness';
import {
	CLOCK_PASS_COPY,
	CLOCK_PASS_QR_PREVIEW,
	CLOCK_PASS_SCAN_FEEDBACK,
	CLOCK_PASS_SCAN_POLL,
	buildClockScanFeedbackMessage,
	getClockActionFromLabel,
	getSecondsUntil,
} from './clockPassHelpers';

const ClockPassView = () => {
	const styles = useThemedStyles(createClockPassViewStyles);
	const { isAVC } = useUserContext();
	const { isMyDayEnabled } = useDriverShell();
	const day = useDriverDay();
	const insets = useSafeAreaInsets();
	const { width, height } = useWindowDimensions();
	const [clockQrToken, setClockQrToken] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [secondsLeft, setSecondsLeft] = useState(0);
	const [isQrPreviewVisible, setIsQrPreviewVisible] = useState(false);
	const [scanFeedbackMessage, setScanFeedbackMessage] = useState('');
	const [shiftSummary, setShiftSummary] = useState(null);
	const [isShiftSummaryVisible, setIsShiftSummaryVisible] = useState(false);
	const canAccessClockPass = Boolean(isAVC);
	// Back label names the phase or Home the pass was opened from ("Home", "End of day").
	const backLabel = useNavigationState((state) => {
		const previousRoute = state?.routes?.[(state.index ?? state.routes.length - 1) - 1];
		return APP_TABS[ROUTE_TAB[previousRoute?.name]]?.label;
	});

	const [createClockQrToken, { loading }] = useMutation(CREATE_CLOCK_QR_TOKEN);
	const [loadClockPassScanState] = useLazyQuery(GET_MY_CLOCK_PASS_SCAN_STATE, {
		fetchPolicy: 'network-only',
	});
	const latestClockTokenRequestRef = useRef(0);
	const latestAcknowledgedClockEventIdRef = useRef('');
	const latestAcknowledgedCapturedAtRef = useRef(null);
	const clockPassOpenedAtRef = useRef(new Date());

	const refreshClockToken = useCallback(async () => {
		const requestId = latestClockTokenRequestRef.current + 1;
		latestClockTokenRequestRef.current = requestId;
		setErrorMessage('');

		try {
			const response = await createClockQrToken({
				variables: {
					input: {},
				},
			});

			if (latestClockTokenRequestRef.current !== requestId) {
				return;
			}

			const nextToken = response?.data?.createClockQrToken || null;

			setClockQrToken(nextToken);
			setSecondsLeft(getSecondsUntil(nextToken?.expiresAt));
		}
		catch (error) {
			if (latestClockTokenRequestRef.current !== requestId) {
				return;
			}

			setErrorMessage(error?.message || CLOCK_PASS_COPY.ERROR_GENERATE_QR);
		}
	}, [createClockQrToken]);

	const { data: scanSubscriptionData } = useSubscription(SUBSCRIBE_TO_CLOCK_EVENT_SCAN, {
		variables: {
			input: {},
		},
		skip: !canAccessClockPass,
	});

	useEffect(() => {
		if (!canAccessClockPass) {
			setClockQrToken(null);
			setSecondsLeft(0);
			setIsQrPreviewVisible(false);
			setScanFeedbackMessage('');
			setShiftSummary(null);
			setIsShiftSummaryVisible(false);
			latestAcknowledgedClockEventIdRef.current = '';
			latestAcknowledgedCapturedAtRef.current = null;
			clockPassOpenedAtRef.current = new Date();
			return undefined;
		}

		setClockQrToken(null);
		setSecondsLeft(0);
		latestAcknowledgedCapturedAtRef.current = null;
		clockPassOpenedAtRef.current = new Date();
		refreshClockToken();
	}, [canAccessClockPass, refreshClockToken]);

	useEffect(() => {
		if (!canAccessClockPass || !clockQrToken?.expiresAt) {
			return undefined;
		}

		const interval = setInterval(() => {
			const currentSecondsLeft = getSecondsUntil(clockQrToken.expiresAt);
			setSecondsLeft(currentSecondsLeft);

			if (currentSecondsLeft <= 0 && !loading) {
				refreshClockToken();
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [canAccessClockPass, clockQrToken?.expiresAt, loading, refreshClockToken]);

	useQrPreviewBrightness(isQrPreviewVisible);

	const actionLabel = useMemo(() => {
		const resolvedActionLabel = String(clockQrToken?.action || '').trim();
		return resolvedActionLabel || CLOCK_PASS_COPY.DEFAULT_ACTION_LABEL;
	}, [clockQrToken?.action]);
	const qrSize = useMemo(() => {
		const maxSize = 230;
		const minSize = 172;
		const availableSize = width - 120;
		return Math.max(minSize, Math.min(maxSize, availableSize));
	}, [width]);
	const qrPreviewSize = useMemo(() => {
		const shortestScreenSide = Math.min(width, height);
		const maxByWidth = Math.max(200, width - 52);
		const maxByHeight = Math.max(200, height - 216);
		const maxSize = Math.min(shortestScreenSide * 0.92, maxByWidth, maxByHeight, 560);

		return Math.max(200, Math.floor(maxSize));
	}, [height, width]);
	const qrPreviewCloseButtonTop = useMemo(() => {
		const statusBarInset = Platform.OS === 'android'
			? Number(StatusBar.currentHeight || 0)
			: 0;
		const topInset = Math.max(Number(insets.top || 0), statusBarInset);

		return topInset + CLOCK_PASS_QR_PREVIEW.CLOSE_BUTTON_MARGIN_TOP;
	}, [insets.top]);
	const openQrPreview = useCallback(() => {
		if (!clockQrToken?.token) {
			return;
		}

		setIsQrPreviewVisible(true);
	}, [clockQrToken?.token]);
	const closeQrPreview = useCallback(() => {
		setIsQrPreviewVisible(false);
	}, []);
	const closeShiftSummary = useCallback(() => {
		setIsShiftSummaryVisible(false);
	}, []);
	const tokenClockAction = useMemo(() => {
		return getClockActionFromLabel(clockQrToken?.action);
	}, [clockQrToken?.action]);
	const handleClockInActionPress = useCallback(() => {
		if (tokenClockAction && tokenClockAction !== CLOCK_EVENT_ACTION.CLOCK_IN) {
			return;
		}

		refreshClockToken();
	}, [refreshClockToken, tokenClockAction]);
	const handleClockOutActionPress = useCallback(() => {
		if (tokenClockAction && tokenClockAction !== CLOCK_EVENT_ACTION.CLOCK_OUT) {
			return;
		}

		refreshClockToken();
	}, [refreshClockToken, tokenClockAction]);
	const handleClockPassScanState = useCallback((nextScanState) => {
		const nextClockEvent = nextScanState?.clockEvent;
		const nextClockEventId = String(nextClockEvent?.id || '').trim();
		if (!nextClockEventId || latestAcknowledgedClockEventIdRef.current === nextClockEventId) {
			return;
		}

		latestAcknowledgedClockEventIdRef.current = nextClockEventId;
		latestAcknowledgedCapturedAtRef.current = nextClockEvent?.capturedAt || new Date();
		setIsQrPreviewVisible(false);
		Vibration.vibrate(CLOCK_PASS_SCAN_FEEDBACK.VIBRATION_MS);
		setScanFeedbackMessage(buildClockScanFeedbackMessage(nextClockEvent));
		// Rotate to the next server-resolved action immediately after a scan.
		setClockQrToken(null);
		setSecondsLeft(0);
		refreshClockToken();

		if (Number(nextClockEvent?.action) !== CLOCK_EVENT_ACTION.CLOCK_OUT) {
			return;
		}

		const providedShiftSummary = nextScanState?.shiftSummary;
		setShiftSummary(providedShiftSummary || {
			clockInAt: null,
			clockOutAt: nextClockEvent?.capturedAt || null,
			workedMinutes: null,
		});
		setIsShiftSummaryVisible(true);
	}, [refreshClockToken]);
	useEffect(() => {
		const scanState = scanSubscriptionData?.ClockEventScanSubscription;
		if (!scanState?.clockEvent) {
			return;
		}

		handleClockPassScanState(scanState);
	}, [handleClockPassScanState, scanSubscriptionData]);

	useEffect(() => {
		if (!canAccessClockPass) {
			return;
		}

		let isMounted = true;
		const fetchLatestScanState = async () => {
			try {
				const response = await loadClockPassScanState({
					variables: {
						since: latestAcknowledgedCapturedAtRef.current || clockPassOpenedAtRef.current,
					},
				});
				if (!isMounted) {
					return;
				}

				const nextScanState = response?.data?.getMyClockPassScanState;
				if (!nextScanState?.clockEvent) {
					return;
				}

				handleClockPassScanState(nextScanState);
			}
			catch (error) {
				// Keep polling; subscription remains the primary delivery path.
			}
		};
		const handleAppStateChange = (nextAppState) => {
			if (nextAppState === 'active') {
				fetchLatestScanState();
			}
		};

		fetchLatestScanState();
		const interval = setInterval(fetchLatestScanState, CLOCK_PASS_SCAN_POLL.INTERVAL_MS);
		const appStateSubscription = AppState.addEventListener('change', handleAppStateChange);

		return () => {
			isMounted = false;
			clearInterval(interval);
			appStateSubscription?.remove?.();
		};
	}, [canAccessClockPass, handleClockPassScanState, loadClockPassScanState]);
	const activeClockAction = tokenClockAction;
	const isClockInActionSelected = activeClockAction === CLOCK_EVENT_ACTION.CLOCK_IN;
	const isClockOutActionSelected = activeClockAction === CLOCK_EVENT_ACTION.CLOCK_OUT;
	const hasResolvedClockAction = tokenClockAction === CLOCK_EVENT_ACTION.CLOCK_IN || tokenClockAction === CLOCK_EVENT_ACTION.CLOCK_OUT;
	const isClockInActionDisabled = loading || (hasResolvedClockAction && tokenClockAction !== CLOCK_EVENT_ACTION.CLOCK_IN);
	const isClockOutActionDisabled = loading || (hasResolvedClockAction && tokenClockAction !== CLOCK_EVENT_ACTION.CLOCK_OUT);
	// Only the user's own driver day has End of day steps (not training, tenants without My day or legacy UI).
	const showsClockOutStepsWarning = isClockOutActionSelected &&
		isMyDayEnabled &&
		day.isEnabled &&
		!day.isTraining &&
		Boolean(day.serviceRun);

	const shiftPassScreenProps = {
		backLabel,
		heading: CLOCK_PASS_COPY.TITLE,
	};

	if (!canAccessClockPass) {
		return (
			<Screen {...shiftPassScreenProps}>
				<EmptyState icon="lock" title={CLOCK_PASS_COPY.TITLE} message={CLOCK_PASS_COPY.RESTRICTED_TEXT} />
			</Screen>
		);
	}

	const actionOptions = [
		{
			key: 'clockIn',
			label: CLOCK_PASS_COPY.ACTION_SELECTOR_CLOCK_IN,
			isSelected: isClockInActionSelected,
			disabled: isClockInActionDisabled,
			onPress: handleClockInActionPress,
		},
		{
			key: 'clockOut',
			label: CLOCK_PASS_COPY.ACTION_SELECTOR_CLOCK_OUT,
			isSelected: isClockOutActionSelected,
			disabled: isClockOutActionDisabled,
			onPress: handleClockOutActionPress,
		},
	];
	let codeStatusText = CLOCK_PASS_COPY.REFRESHING_CODE;
	if (!loading && secondsLeft > 0) {
		codeStatusText = `${actionLabel} ${CLOCK_PASS_COPY.EXPIRES_SUFFIX} ${secondsLeft}s`;
	}

	return (
		<>
			<Screen {...shiftPassScreenProps} subheading={CLOCK_PASS_COPY.SUBTITLE}>
				{/* The server decides whether the next scan clocks in or out; the other option stays disabled. */}
				<View style={styles.actionSelector} accessibilityRole="radiogroup">
					{actionOptions.map(option => (
						<Pressable
							key={option.key}
							onPress={option.onPress}
							disabled={option.disabled}
							accessibilityRole="radio"
							accessibilityLabel={option.label}
							accessibilityState={{ selected: option.isSelected, checked: option.isSelected, disabled: option.disabled }}
							style={({ pressed }) => [
								styles.actionOption,
								option.isSelected && styles.actionOptionSelected,
								pressed && styles.pressed,
							]}
						>
							<Text
								style={[
									styles.actionOptionText,
									option.isSelected && styles.actionOptionTextSelected,
									option.disabled && !option.isSelected && styles.actionOptionTextDisabled,
								]}
							>
								{option.label}
							</Text>
						</Pressable>
					))}
				</View>

				{showsClockOutStepsWarning ? <ClockOutStepsWarning day={day} /> : null}

				<View style={styles.qrCard}>
					{clockQrToken?.token ? (
						<TouchableOpacity
							style={styles.qrTapTarget}
							activeOpacity={0.9}
							onPress={openQrPreview}
							accessibilityRole="button"
							accessibilityLabel={`${actionLabel} code`}
							accessibilityHint={CLOCK_PASS_COPY.TAP_TO_ENLARGE}
						>
							<QRCode
								value={clockQrToken.token}
								size={qrSize}
								quietZone={10}
							/>
						</TouchableOpacity>
					) : (
						<View style={styles.qrPlaceholder}>
							<Text style={styles.qrPlaceholderText}>{CLOCK_PASS_COPY.GENERATING_CODE}</Text>
						</View>
					)}
					<Text style={styles.codeStatusText}>{codeStatusText}</Text>
					{clockQrToken?.token ? (
						<Text style={styles.qrTapHint}>{CLOCK_PASS_COPY.TAP_TO_ENLARGE}</Text>
					) : null}
				</View>

				<View style={styles.scanStatusCard}>
					<Text style={styles.scanStatusLabel}>{CLOCK_PASS_COPY.LAST_SCAN_LABEL}</Text>
					<Text style={[styles.scanFeedbackText, !scanFeedbackMessage && styles.scanFeedbackEmpty]}>
						{scanFeedbackMessage || CLOCK_PASS_COPY.LAST_SCAN_EMPTY}
					</Text>
				</View>
				{errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}

				<SecondaryButton
					label={CLOCK_PASS_COPY.REFRESH_CODE_BUTTON}
					icon="refresh-cw"
					onPress={() => refreshClockToken()}
					loading={loading}
				/>
			</Screen>

			<Modal
				visible={isQrPreviewVisible}
				animationType="fade"
				transparent={false}
				statusBarTranslucent={true}
				onRequestClose={closeQrPreview}
			>
				<View style={styles.qrPreviewModalRoot}>
					<TouchableOpacity
						style={[
							styles.qrPreviewCloseButton,
							{ top: qrPreviewCloseButtonTop },
						]}
						onPress={closeQrPreview}
						accessibilityRole="button"
					>
						<Text style={styles.qrPreviewCloseButtonText}>{CLOCK_PASS_COPY.FULLSCREEN_CLOSE_BUTTON}</Text>
					</TouchableOpacity>

					<View style={styles.qrPreviewBody}>
						<TouchableOpacity
							style={styles.qrPreviewFrame}
							activeOpacity={1}
							onPress={closeQrPreview}
						>
							{clockQrToken?.token ? (
								<QRCode
									value={clockQrToken.token}
									size={qrPreviewSize}
									quietZone={10}
								/>
							) : (
								<Text style={styles.qrPlaceholderText}>{CLOCK_PASS_COPY.GENERATING_CODE}</Text>
							)}
						</TouchableOpacity>
						<Text style={styles.qrPreviewActionText}>{actionLabel}</Text>
					</View>
				</View>
			</Modal>

			<ShiftSummaryModal
				visible={isShiftSummaryVisible}
				shiftSummary={shiftSummary}
				onClose={closeShiftSummary}
			/>
		</>
	);
};

export default React.memo(ClockPassView);
